/**
 * frontend/src/player/PlayerContext.jsx
 * SoundDAO Sprint 6A — the ONE app-level audio player.
 *
 * A single <audio> element lives here for the whole app; every screen plays through
 * usePlayer(). Archive.org audio is played by the element directly: it is NEVER routed
 * through the Web Audio API (cross-origin media would need CORS and could go silent).
 *
 * Queue entries: { key, title, artist, streamUrl, archiveUrl, identifier }.
 * On a media error the track shows "Unavailable" and the player skips to the next one.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { track } from '../lib/analytics';

const PlayerContext = createContext(null);
const SKIP_DELAY_MS = 1500;

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const skipTimer = useRef(null);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(-1);
  const [status, setStatus] = useState('idle');   // idle | loading | playing | paused | error
  const [progress, setProgress] = useState({ current: 0, duration: 0 });

  const current = index >= 0 ? queue[index] || null : null;
  const currentKey = current ? current.key : null;
  const currentRef = useRef(current);
  currentRef.current = current;

  const clearSkip = () => {
    if (skipTimer.current) { clearTimeout(skipTimer.current); skipTimer.current = null; }
  };

  // Load + play whenever the current TRACK changes (by key, so re-queuing the same
  // track does not restart it).
  useEffect(() => {
    const current = currentRef.current;
    const el = audioRef.current;
    clearSkip();
    if (!el) return;
    if (!current) {
      el.pause();
      el.removeAttribute('src');
      el.load();
      setStatus('idle');
      setProgress({ current: 0, duration: 0 });
      return;
    }
    el.src = current.streamUrl;
    track('track_played', { source: current.from || 'dig' });
    setStatus('loading');
    setProgress({ current: 0, duration: 0 });
    const p = el.play();
    if (p && typeof p.catch === 'function') {
      p.catch((err) => {
        // Autoplay refusal is not a broken track: wait for the user to press play.
        if (err && err.name === 'NotAllowedError') setStatus('paused');
      });
    }
    if ('mediaSession' in navigator && typeof window.MediaMetadata === 'function') {
      try {
        navigator.mediaSession.metadata = new window.MediaMetadata({ title: current.title, artist: current.artist, album: 'Internet Archive' });
      } catch { /* optional */ }
    }
  }, [currentKey]);

  const next = useCallback(() => {
    clearSkip();
    setIndex((i) => (i + 1 < queue.length ? i + 1 : i));
  }, [queue.length]);

  const prev = useCallback(() => {
    const el = audioRef.current;
    clearSkip();
    if (el && el.currentTime > 3) { el.currentTime = 0; return; }
    setIndex((i) => (i > 0 ? i - 1 : i));
  }, []);

  const playQueue = useCallback((tracks, startIndex = 0) => {
    if (!tracks || !tracks.length) return;
    clearSkip();
    const i = Math.min(Math.max(0, startIndex), tracks.length - 1);
    const el = audioRef.current;
    if (current && el && tracks[i].key === current.key) {
      // Same track: keep the element, just (re)start it and adopt the new queue.
      setQueue(tracks);
      setIndex(i);
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
      return;
    }
    setQueue(tracks);
    setIndex(i);
  }, [current]);

  const toggle = useCallback(() => {
    const el = audioRef.current;
    if (!el || !current) return;
    if (el.paused) {
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      el.pause();
    }
  }, [current]);

  const clear = useCallback(() => {
    clearSkip();
    setQueue([]);
    setIndex(-1);
  }, []);

  const seek = useCallback((fraction) => {
    const el = audioRef.current;
    if (el && Number.isFinite(el.duration) && el.duration > 0) el.currentTime = fraction * el.duration;
  }, []);

  // Audio element events.
  const onError = () => {
    if (!current) return;
    setStatus('error');
    clearSkip();
    skipTimer.current = setTimeout(() => {
      skipTimer.current = null;
      setIndex((i) => {
        if (i + 1 < queue.length) return i + 1;
        return i;   // last track: stay on "Unavailable"
      });
    }, SKIP_DELAY_MS);
  };
  const onEnded = () => {
    if (index + 1 < queue.length) setIndex(index + 1);
    else setStatus('paused');
  };

  useEffect(() => {
    if (!('mediaSession' in navigator)) return;
    try {
      navigator.mediaSession.setActionHandler('nexttrack', next);
      navigator.mediaSession.setActionHandler('previoustrack', prev);
    } catch { /* optional */ }
  }, [next, prev]);

  useEffect(() => () => clearSkip(), []);

  const value = useMemo(() => ({
    queue, index, current, status, progress,
    playQueue, toggle, next, prev, clear, seek,
    hasNext: index + 1 < queue.length,
    hasPrev: index > 0,
    isCurrent: (key) => Boolean(current && current.key === key),
  }), [queue, index, current, status, progress, playQueue, toggle, next, prev, clear, seek]);

  return (
    <PlayerContext.Provider value={value}>
      {children}
      {/* The single app-level audio element. No crossOrigin, no Web Audio graph. */}
      <audio
        ref={audioRef}
        preload="none"
        onPlaying={() => setStatus('playing')}
        onPause={() => setStatus((s) => (s === 'error' ? s : 'paused'))}
        onWaiting={() => setStatus('loading')}
        onError={onError}
        onEnded={onEnded}
        onTimeUpdate={(e) => setProgress({ current: e.currentTarget.currentTime || 0, duration: e.currentTarget.duration || 0 })}
        style={{ display: 'none' }}
      />
    </PlayerContext.Provider>
  );
}

export function usePlayer() {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error('usePlayer must be used inside <PlayerProvider>');
  return ctx;
}
